import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  createdAt: string
  actionUrl?: string
  actionText?: string
}

export interface Modal {
  id: string
  type: string
  isOpen: boolean
  data?: any
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
}

export interface Toast {
  id: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

export interface UIState {
  loading: boolean
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  themeMode: 'light' | 'dark' | 'system'
  notifications: Notification[]
  modals: Modal[]
  toasts: Toast[]
  activeModal: string | null
  searchQuery: string
  searchResults: any[]
  searchLoading: boolean
  pageTitle: string
  breadcrumbs: Array<{
    label: string
    href?: string
  }>
}

const initialState: UIState = {
  loading: false,
  sidebarOpen: false,
  sidebarCollapsed: false,
  themeMode: 'system',
  notifications: [
    {
      id: '1',
      title: 'Yeni Rozet Kazandın!',
      message: 'Çalışkan Öğrenci rozeti hesabına eklendi.',
      type: 'success',
      read: false,
      createdAt: new Date().toISOString(),
      actionUrl: '/gamification/badges',
      actionText: 'Görüntüle'
    },
    {
      id: '2',
      title: 'Sınav Hatırlatması',
      message: 'Matematik deneme sınavın 1 saat sonra başlayacak.',
      type: 'warning',
      read: false,
      createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      actionUrl: '/exams/trials',
      actionText: 'Sınava Git'
    },
    {
      id: '3',
      title: 'Çalışma Planı Güncellendi',
      message: 'Bu haftanın çalışma planı başarıyla güncellendi.',
      type: 'info',
      read: true,
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      actionUrl: '/study/plans',
      actionText: 'Planı Gör'
    }
  ],
  modals: [],
  toasts: [],
  activeModal: null,
  searchQuery: '',
  searchResults: [],
  searchLoading: false,
  pageTitle: 'LGS Takip',
  breadcrumbs: []
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Sidebar
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleSidebarOpen: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },

    // Theme
    setThemeMode: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.themeMode = action.payload
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'createdAt'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      }
      state.notifications.unshift(notification)
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true
      })
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },

    // Modals
    openModal: (state, action: PayloadAction<Omit<Modal, 'id' | 'isOpen'>>) => {
      const modal: Modal = {
        ...action.payload,
        id: Date.now().toString(),
        isOpen: true
      }
      state.modals.push(modal)
      state.activeModal = modal.id
    },
    closeModal: (state, action: PayloadAction<string>) => {
      const modalIndex = state.modals.findIndex(m => m.id === action.payload)
      if (modalIndex !== -1) {
        state.modals[modalIndex].isOpen = false
        if (state.activeModal === action.payload) {
          const openModal = state.modals.find(m => m.isOpen)
          state.activeModal = openModal?.id || null
        }
      }
    },
    closeAllModals: (state) => {
      state.modals.forEach(modal => {
        modal.isOpen = false
      })
      state.activeModal = null
    },
    removeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(m => m.id !== action.payload)
      if (state.activeModal === action.payload) {
        const openModal = state.modals.find(m => m.isOpen)
        state.activeModal = openModal?.id || null
      }
    },

    // Toasts
    addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      const toast: Toast = {
        ...action.payload,
        id: Date.now().toString()
      }
      state.toasts.push(toast)
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(t => t.id !== action.payload)
    },
    clearToasts: (state) => {
      state.toasts = []
    },

    // Search
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setSearchResults: (state, action: PayloadAction<any[]>) => {
      state.searchResults = action.payload
    },
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.searchLoading = action.payload
    },
    clearSearch: (state) => {
      state.searchQuery = ''
      state.searchResults = []
      state.searchLoading = false
    },

    // Page
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload
    },
    setBreadcrumbs: (state, action: PayloadAction<Array<{ label: string; href?: string }>>) => {
      state.breadcrumbs = action.payload
    },
    addBreadcrumb: (state, action: PayloadAction<{ label: string; href?: string }>) => {
      state.breadcrumbs.push(action.payload)
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = []
    }
  }
})

export const {
  setLoading,
  setSidebarOpen,
  toggleSidebarOpen,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  setThemeMode,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  closeAllModals,
  removeModal,
  addToast,
  removeToast,
  clearToasts,
  setSearchQuery,
  setSearchResults,
  setSearchLoading,
  clearSearch,
  setPageTitle,
  setBreadcrumbs,
  addBreadcrumb,
  clearBreadcrumbs
} = uiSlice.actions

export default uiSlice.reducer